import {useCallback, useEffect, useRef, useState} from 'react';

import {assert} from 'Asserts';
import deepCopy from 'lib/deepCopy';
import {MethodUpdater} from 'procedures/design/ProcedureEditor';
import {Method} from 'procedures/ProcedureDomainTypes';
import MethodSidepanelContext from 'procedures/processes/MethodSidepanelContext';
import {ProcedurePauseCondition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import {useFlowchartSidepanelTabUnsavedChanges} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/right-sidepanel/useFlowchartSidepanelTabUnsavedChanges';

export interface UseDraftMethodEditorParams {
  method: Method;
  onSave: (method: Method) => Promise<void>;
  pauseCondition: ProcedurePauseCondition;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
}

export interface UseDraftMethodEditorReturn {
  isEditing: boolean;
  onStartEditing: () => void;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  onUpdateMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  methodDuringEditing: Method;
}

/**
 * This hook is a simple draft-state manager for MODAL edits of a method. "Modal" means it has clearly
 * defined start and end actions. And "end actions" includes both save and cancel options.
 *
 * In practice, this is used when editing a method's Definitions and Test Orders.
 *
 * The hook keeps track of the method data prior to initiating edits, so we can revert if the user cancels.
 * It also manages the unsavedChanges hook, which the workflows platform offers as a way to ensure users
 * don't lose unsaved changes when tabbing away or closing the sidepanel.
 *
 * We also temporarily pause the autosaver while editing is in progress.
 *
 * NOTE: This hook is only usable in the PoW method editor. Legacy procedures has its own strategy for this.
 */
export default function useDraftMethodEditor({
  method,
  onSave: parentOnSave,
  onChangeMethod: parentOnChangeMethod,
  pauseCondition,
}: UseDraftMethodEditorParams): UseDraftMethodEditorReturn {
  const [isEditing, setIsEditing] = useState(false);
  const [draftMethod, setDraftMethod] = useState<Method | null>(null);
  const methodPriorToOpeningEditor = useRef<Method | null>(null);
  const {pauseAutosaver} = MethodSidepanelContext.useContext();

  assert(pauseAutosaver != null, 'useDraftMethodEditor can only run in a PoW context');

  /**
   * Our onSave handler is really only for managing the pause control. It can be called directly (when
   * the user presses Save) or indirectly (by the unsavedChanges hook below)
   */
  const handleOnSave = useCallback(async () => {
    await parentOnSave(draftMethod!);
    setIsEditing(false);
    setDraftMethod(null);
    pauseAutosaver(pauseCondition, false);
  }, [parentOnSave, draftMethod, pauseAutosaver, pauseCondition]);

  /**
   * Our onDiscard handler mainly manages reverting to the old method data
   */
  const handleOnDiscard = useCallback(() => {
    setIsEditing(false);
    setDraftMethod(null);

    parentOnChangeMethod(method.cid, () => {
      const revertTo = methodPriorToOpeningEditor.current!;
      methodPriorToOpeningEditor.current = null;
      pauseAutosaver(pauseCondition, false);
      return revertTo;
    });
  }, [parentOnChangeMethod, pauseAutosaver, pauseCondition, method.cid]);

  // This hook is provided by workflows, and presents a confirmation modal if the user tries to navigate
  // away with unsaved changes.
  // TODO(BNCH-137829): This currently only works when the user tries to close the sidepanel. It also
  //  needs to present the confirmation modal when the user changes tabs /within/ the sidepanel
  const {setIsTabDirty} = useFlowchartSidepanelTabUnsavedChanges(
    `method-${method.cid}`,
    handleOnSave,
    handleOnDiscard
  );

  const handleOnStartEditing = useCallback(() => {
    pauseAutosaver(pauseCondition, true);
    setIsEditing(true);

    const methodForOnCancel = deepCopy(method);
    methodPriorToOpeningEditor.current = methodForOnCancel;
    setDraftMethod(methodForOnCancel);

    // This notion of dirty is not the same as the procedure's internal dirty state. It only answers
    // the question: has the user made any change to this tab since starting the edit operation?
    setIsTabDirty(false);
  }, [method, pauseAutosaver, pauseCondition, setIsTabDirty]);

  const handleOnUpdateMethod = useCallback(
    (cid: string, methodUpdater: MethodUpdater): void => {
      // Track in local state while editing
      if (isEditing && draftMethod != null) {
        setDraftMethod(methodUpdater(draftMethod));
        setIsTabDirty(true);
      }
      parentOnChangeMethod(cid, methodUpdater);
    },
    [isEditing, draftMethod, parentOnChangeMethod, setIsTabDirty]
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      pauseAutosaver(pauseCondition, false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    isEditing,
    onStartEditing: handleOnStartEditing,
    onSave: handleOnSave,
    onDiscard: handleOnDiscard,
    onUpdateMethod: handleOnUpdateMethod,
    methodDuringEditing: draftMethod ?? method,
  };
}
